import { getAddress } from 'viem';
import {
    readContract,
    switchChain,
    waitForTransactionReceipt,
    writeContract,
} from 'wagmi/actions';

import { walletWagmiConfig } from '@/features/wallet';
import {
    anchorLabel,
    DEFAULT_ANCHOR_CHAIN_ID,
    isAnchorChainId,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import {
    bridgeIntentDescriptorSchema,
    SPEED_NETWORK_ID,
} from '@/lib/api/speed';
import type { BridgeIntentDescriptor, SpeedJobResponse } from '@/lib/api/speed';
import {
    MIN_BRIDGE_INTENT_REGISTRY_VERSION,
    SERVICE_WALLET_REGISTRY_ABI,
    serviceWalletRegistryFor,
} from '@/lib/config/serviceWalletRegistry';

/**
 * The one on-chain transaction a withdrawal asks the user for.
 *
 * A withdrawal's funds leave through a service wallet that pushes them into a
 * bridge, and until now nothing on chain said WHO that hop was supposed to pay:
 * the bridge request id named a route the backend had quoted, and a backend
 * that lied could have quoted itself. So before the hop may spend anything, the
 * user's own wallet records the hop in the `ServiceWalletRegistry` on the
 * session's AUTHORIZATION ANCHOR: bridge request id, token, amount, origin and
 * destination chain, recipient. RustSigner reads both anchors' registries and
 * refuses to sign a bridge deposit whose calldata matches neither entry.
 *
 * What that does NOT cover, stated plainly: the quote behind the id is still
 * obtained by the backend, so the registration pins the id, the amount and the
 * recipient, but not that the bridge request behind the id really pays that
 * recipient. Closing that needs a second party to fetch the quote.
 *
 * Everything here runs on the user's connected EOA on the anchor chain (25 or
 * 4663), which is where the app keeps their wallet anyway.
 */

/** What the user actually asked for, to check the descriptor against. */
export interface BridgeIntentCheckContext {
    /** Connected wallet address; the funds must come back here. */
    connectedAddress: string | null | undefined;
    /** Base-unit amount (USDC 6dp) the user asked to withdraw. */
    amount: bigint | null | undefined;
    /**
     * USDC on HyperEVM, as this app resolves it (`resolveHyperUsdcAddress`).
     * Passed in rather than read here: the address comes from an async backend
     * read and the caller already has the API clients for it.
     */
    hyperUsdcAddress: string | null | undefined;
    /**
     * The anchor this withdrawal runs under. The funds land there and the intent
     * is recorded in that anchor's registry, so a descriptor naming any other
     * chain is refused.
     */
    anchorChainId?: AnchorChainId;
}

function sameAddress(
    a: string | null | undefined,
    b: string | null | undefined,
): boolean {
    return (
        typeof a === 'string' &&
        typeof b === 'string' &&
        a.toLowerCase() === b.toLowerCase()
    );
}

/**
 * Refuse to register anything that is not the withdrawal the user asked for.
 *
 * The registration is what AUTHORIZES the hop, so a descriptor that slipped a
 * different recipient, token, chain or registry past this check would be the
 * user signing away their own funds. RustSigner re-checks the same values
 * against the bridge calldata; this exists so the wallet prompt never opens for
 * a hop that is wrong in the first place.
 */
export function assertBridgeIntentMatchesIntent(
    descriptor: BridgeIntentDescriptor,
    context: BridgeIntentCheckContext,
): void {
    // The schema narrows `bridgeId` to the ids this app knows
    // (`SUPPORTED_BRIDGE_IDS`), so an intent for any other bridge fails here.
    // That matters: the intent store is namespaced by bridge id, and an id no
    // signer reads means the user pays gas to authorize nothing.
    const parsed = bridgeIntentDescriptorSchema.safeParse(descriptor);
    if (!parsed.success) {
        throw new Error(
            'The server described this withdrawal in an unexpected shape. ' +
                'Nothing was signed. Please try again.',
        );
    }
    const intent = parsed.data;

    if (!context.connectedAddress) {
        throw new Error(
            'Connect the wallet that owns this account to confirm the ' +
                'withdrawal, then try again.',
        );
    }
    if (!sameAddress(intent.recipient, context.connectedAddress)) {
        throw new Error(
            'The server asked to send this withdrawal to a different ' +
                'address than your connected wallet. Nothing was signed.',
        );
    }

    if (context.amount === null || context.amount === undefined) {
        throw new Error(
            'Could not confirm how much this withdrawal moves. Nothing was ' +
                'signed. Please try again.',
        );
    }
    const amount = BigInt(intent.amount);
    if (amount <= 0n) {
        throw new Error(
            'The server described this withdrawal with no amount. Nothing ' +
                'was signed.',
        );
    }
    // Bounded, not equal. The server caps a withdrawal to what is actually
    // sweepable right now (the app asks for the displayed balance, which can
    // exceed it by the core-sweep reserve), so a legitimate hop is routinely
    // for slightly LESS than the figure typed in. What protects the user is
    // the other direction: never more than they asked to move.
    if (amount > context.amount) {
        throw new Error(
            'The server asked to authorize more than the withdrawal amount ' +
                'requested. Nothing was signed.',
        );
    }

    if (!context.hyperUsdcAddress) {
        throw new Error(
            'Could not confirm which token this withdrawal moves. Nothing ' +
                'was signed. Please try again in a moment.',
        );
    }
    if (!sameAddress(intent.token, context.hyperUsdcAddress)) {
        throw new Error(
            'The server asked to authorize a different token than the one ' +
                'this account trades with. Nothing was signed.',
        );
    }

    if (intent.originChainId !== SPEED_NETWORK_ID) {
        throw new Error(
            'The server asked to bridge this withdrawal from an unexpected ' +
                'network. Nothing was signed.',
        );
    }
    const anchorChainId = context.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    if (intent.destinationChainId !== anchorChainId) {
        throw new Error(
            'The server asked to send this withdrawal to an unexpected ' +
                'network. Nothing was signed.',
        );
    }
    // the registry chain must be an anchor AND the one this withdrawal runs on:
    // recording the hop anywhere else leaves the signer reading an empty entry.
    if (
        !isAnchorChainId(intent.registryChainId) ||
        intent.registryChainId !== anchorChainId
    ) {
        throw new Error(
            'The server asked to record this withdrawal on an unexpected ' +
                'network. Nothing was signed.',
        );
    }
    let expectedRegistry: string | null = null;
    try {
        expectedRegistry = serviceWalletRegistryFor(intent.registryChainId);
    } catch {
        // no registry deployed on that anchor yet
        expectedRegistry = null;
    }
    if (!expectedRegistry || !sameAddress(intent.registry, expectedRegistry)) {
        throw new Error(
            'The server asked to record this withdrawal in an unexpected ' +
                'contract. Nothing was signed.',
        );
    }
}

/**
 * Reads the bridge intent off a job that is waiting for one
 * (`status: 'actionRequired'`, `step: 'awaitingBridgeIntent'`).
 *
 * The job's `response` is free-form, so the descriptor is validated here rather
 * than trusted: it drives a transaction from the user's own wallet.
 * {@link assertBridgeIntentMatchesIntent} still has to run on the result.
 */
export function bridgeIntentFromJob(
    job: SpeedJobResponse,
): BridgeIntentDescriptor | null {
    const parsed = bridgeIntentDescriptorSchema.safeParse(
        job.response?.bridgeIntent,
    );
    return parsed.success ? parsed.data : null;
}

/** What `getBridgeIntent` answers with; `registeredAt` 0 means "no entry". */
interface StoredBridgeIntent {
    originChainId: bigint;
    destinationChainId: bigint;
    registeredAt: bigint;
    token: string;
    recipient: string;
    amount: bigint;
}

/** The entry the registry holds for this hop, or null if it cannot be read. */
async function readStoredBridgeIntent(
    descriptor: BridgeIntentDescriptor,
    registryChainId: AnchorChainId,
): Promise<StoredBridgeIntent | null> {
    try {
        const stored = await readContract(walletWagmiConfig, {
            address: getAddress(descriptor.registry),
            abi: SERVICE_WALLET_REGISTRY_ABI,
            functionName: 'getBridgeIntent',
            chainId: registryChainId,
            args: [
                getAddress(descriptor.recipient),
                descriptor.bridgeId,
                descriptor.id as `0x${string}`,
            ],
        });
        return stored as unknown as StoredBridgeIntent;
    } catch {
        // an unreadable registry is not proof of anything, so the caller keeps
        // treating the failed registration as a failure.
        return null;
    }
}

/**
 * True when the registry ALREADY holds exactly this hop.
 *
 * Every field the registration pins is compared, not just the id: an entry that
 * differs in amount, token, recipient or either chain authorizes a different
 * movement, and accepting it would be the app waving through a hop the user
 * never confirmed.
 */
function storedIntentMatchesDescriptor(
    stored: StoredBridgeIntent,
    descriptor: BridgeIntentDescriptor,
): boolean {
    return (
        stored.registeredAt > 0n &&
        stored.originChainId === BigInt(descriptor.originChainId) &&
        stored.destinationChainId === BigInt(descriptor.destinationChainId) &&
        stored.amount === BigInt(descriptor.amount) &&
        sameAddress(stored.token, descriptor.token) &&
        sameAddress(stored.recipient, descriptor.recipient)
    );
}

async function bridgeIntentAlreadyOnChain(
    descriptor: BridgeIntentDescriptor,
    registryChainId: AnchorChainId,
): Promise<boolean> {
    const stored = await readStoredBridgeIntent(descriptor, registryChainId);
    return !!stored && storedIntentMatchesDescriptor(stored, descriptor);
}

/**
 * Puts the connected wallet on the descriptor's own registry chain and registers
 * the intent, resolving once the hop is recorded on chain.
 *
 * The chain switch is EXPLICIT on purpose: wagmi's `writeContract({ chainId })`
 * asserts the chain for plain injected connectors instead of switching it. A
 * reverted receipt is rejected rather than treated as done, because a
 * registration that did not land means the backend will refuse the hop and the
 * withdrawal would silently stall.
 *
 * ONE revert is not a failure: the registry is write-once per
 * (owner, bridgeId, id), and the deBridge route derives that id from the hop's
 * own fields, so re-quoting the SAME hop hands back the SAME id and a second
 * registration reverts with "already registered". The hop is then already
 * authorized, and the withdrawal continues on it. That is only true if what is
 * stored IS this hop, so the entry is read back and every pinned field
 * compared; a missing or differing entry stays an error.
 *
 * Call {@link assertBridgeIntentMatchesIntent} first: this function registers
 * what it is given, and `recipient` is used as the sending account (they are
 * the same address once the check has passed).
 */
export async function sendBridgeIntentRegistration(
    descriptor: BridgeIntentDescriptor,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<`0x${string}`> {
    if (
        !isAnchorChainId(descriptor.registryChainId) ||
        descriptor.registryChainId !== anchorChainId
    ) {
        throw new Error(
            'The server asked to record this withdrawal on an unexpected ' +
                'network. Nothing was signed.',
        );
    }
    const registryChainId = descriptor.registryChainId;
    await assertRegistryKnowsBridgeIntents(descriptor, registryChainId);

    try {
        await switchChain(walletWagmiConfig, { chainId: registryChainId });
    } catch {
        throw new Error(
            `Switch your wallet to the ${anchorLabel(registryChainId)} ` +
                'network to confirm the withdrawal.',
        );
    }

    const hash = await writeContract(walletWagmiConfig, {
        account: getAddress(descriptor.recipient),
        address: getAddress(descriptor.registry),
        abi: SERVICE_WALLET_REGISTRY_ABI,
        functionName: 'registerBridgeIntent',
        chainId: registryChainId,
        args: [
            descriptor.bridgeId,
            descriptor.id as `0x${string}`,
            BigInt(descriptor.originChainId),
            getAddress(descriptor.token),
            BigInt(descriptor.amount),
            BigInt(descriptor.destinationChainId),
            getAddress(descriptor.recipient),
        ],
    });

    const receipt = await waitForTransactionReceipt(walletWagmiConfig, {
        hash,
        chainId: registryChainId,
    });
    if (receipt.status !== 'success') {
        // A receipt carries no revert reason, and it does not need one: if the
        // registry already holds this exact hop then the hop IS authorized, and
        // why this particular transaction lost the race stops mattering. Any
        // other reverted receipt (no entry, or an entry for a different amount,
        // token, recipient or chain) is still a failure, because then nothing
        // the user confirmed is on chain.
        if (await bridgeIntentAlreadyOnChain(descriptor, registryChainId)) {
            return hash;
        }
        throw new Error('Withdrawal confirmation transaction reverted.');
    }
    return hash;
}

/**
 * A registry that predates bridge intents has no `registerBridgeIntent`; the
 * wallet would sign a transaction that reverts with no reason and the user
 * would pay gas for nothing (Cronos still ran 1_01_00 on 2026-09-02 while
 * Robinhood was on 1_02_00). Read the version first and refuse in plain words.
 * A registry that cannot be read is not evidence of anything, so the
 * registration proceeds and its own receipt decides.
 */
async function assertRegistryKnowsBridgeIntents(
    descriptor: BridgeIntentDescriptor,
    registryChainId: AnchorChainId,
): Promise<void> {
    let version: bigint;
    try {
        version = (await readContract(walletWagmiConfig, {
            address: getAddress(descriptor.registry),
            abi: SERVICE_WALLET_REGISTRY_ABI,
            functionName: 'version',
            chainId: registryChainId,
        })) as bigint;
    } catch {
        return;
    }
    if (version < MIN_BRIDGE_INTENT_REGISTRY_VERSION) {
        throw new Error(
            `Withdrawals to ${anchorLabel(registryChainId)} are not available ` +
                'yet: its registry has not been upgraded. Nothing was signed.',
        );
    }
}
